import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../App';
import { Radio, Mic, X, Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SECONDS = 15;

/**
 * PTTWalkieRecorder — Modal hold-to-record.
 * Presionar y mantener para grabar; soltar para enviar automáticamente.
 * También soporta tap-tap (start → stop) para dispositivos con dificultades de gesto.
 */
const PTTWalkieRecorder = ({ open, target, onClose }) => {
  const { API, getImageUrl } = useContext(AuthContext);
  const [state, setState] = useState('idle'); // idle | recording | uploading
  const [seconds, setSeconds] = useState(0);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const mimeRef = useRef('');
  const stopIntentRef = useRef(false); // true si el usuario soltó = enviar; false = cancelar

  const cleanup = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') mediaRecRef.current.stop();
    } catch (_e) { /* noop */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* noop */ }
    streamRef.current = null;
    setSeconds(0);
  }, []);

  useEffect(() => {
    if (!open) {
      cleanup();
      setState('idle');
    }
  }, [open, cleanup]);

  const uploadAndSend = useCallback(async (blob, durationMs) => {
    if (!target) return;
    setState('uploading');
    try {
      const fd = new FormData();
      const ext = (blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('ogg') ? 'ogg' : 'webm'));
      fd.append('audio', blob, `walkie.${ext}`);
      fd.append('target_user_id', target.id);
      if (durationMs) fd.append('duration_ms', String(durationMs));
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/ptt/walkie`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'No se pudo enviar');
      }
      toast.success(`🎙️ Enviado a ${target.nombre}`);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Error enviando walkie');
      setState('idle');
    }
  }, [API, target, onClose]);

  const startRecord = useCallback(async () => {
    if (state !== 'idle') return;
    stopIntentRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
          : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecRef.current = rec;
      chunksRef.current = [];
      let startedAt = Date.now();
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* noop */ }
        const dur = Date.now() - startedAt;
        const b = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        if (!stopIntentRef.current) {
          setState('idle');
          return;
        }
        if (dur < 500) {
          toast.warning('⚠️ Grabación muy corta. Mantené presionado al menos 1 segundo.');
          setState('idle');
          return;
        }
        if (b.size < 500) {
          toast.error('El audio salió vacío. Probá otra vez y mantené el botón bien apretado.');
          setState('idle');
          return;
        }
        uploadAndSend(b, dur);
      };
      rec.start(250);
      setState('recording');
      setSeconds(0);
      startedAt = Date.now();
      intervalRef.current = setInterval(() => {
        setSeconds(s => {
          const ns = s + 1;
          if (ns >= MAX_SECONDS) {
            // Auto-stop y envío al llegar al límite
            stopIntentRef.current = true;
            try { mediaRecRef.current?.stop(); } catch (_e) { /* noop */ }
            if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
            return MAX_SECONDS;
          }
          return ns;
        });
      }, 1000);
    } catch (e) {
      toast.error('No pude acceder al micrófono');
      setState('idle');
    }
  }, [state, uploadAndSend]);

  const stopAndSend = useCallback(() => {
    if (state !== 'recording') return;
    stopIntentRef.current = true;
    // Forzar flush de datos antes de stop (crítico para grabaciones cortas)
    try { mediaRecRef.current?.requestData?.(); } catch (_e) { /* noop */ }
    try { mediaRecRef.current?.stop(); } catch (_e) { /* noop */ }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  }, [state]);

  const cancelRecord = useCallback(() => {
    if (state !== 'recording') return;
    stopIntentRef.current = false;
    try { mediaRecRef.current?.stop(); } catch (_e) { /* noop */ }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    setState('idle');
    setSeconds(0);
  }, [state]);

  if (!open || !target) return null;

  const getInitials = (u) => `${(u.nombre || '')[0] || ''}${(u.apellido || '')[0] || ''}`.toUpperCase();
  const photoUrl = target.foto_perfil ? (getImageUrl ? getImageUrl(target.foto_perfil) : target.foto_perfil) : '';

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      onClick={() => state === 'idle' && onClose()}
      data-testid="walkie-recorder-modal"
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 text-white px-5 py-4 flex items-center gap-3">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/60" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center font-bold ring-2 ring-white/60">
              {getInitials(target)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs opacity-80 font-medium">Radio · Nota rápida</p>
            <p className="font-bold text-lg truncate">{target.nombre} {target.apellido}</p>
          </div>
          <button
            onClick={() => state === 'idle' && onClose()}
            disabled={state !== 'idle'}
            className="text-white/80 hover:text-white p-1 disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col items-center gap-4">
          {state === 'idle' && (
            <>
              <button
                onClick={startRecord}
                className="w-40 h-40 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
                data-testid="walkie-record-btn"
                aria-label="Tocar para empezar a grabar"
              >
                <Mic className="w-16 h-16" />
              </button>
              <p className="text-center text-gray-700 text-base font-semibold">
                Tocá para empezar a hablar
              </p>
              <p className="text-center text-gray-400 text-xs">
                Después tocá &quot;Enviar&quot; para mandarlo · Máximo {MAX_SECONDS}s
              </p>
            </>
          )}

          {state === 'recording' && (
            <>
              <button
                onClick={stopAndSend}
                className="relative w-40 h-40 rounded-full bg-green-600 text-white shadow-2xl flex flex-col items-center justify-center gap-1 hover:scale-105 active:scale-95 transition-transform"
                data-testid="walkie-tap-send-btn"
                aria-label="Tocar para enviar"
              >
                <div className="absolute inset-0 rounded-full border-4 border-green-300 animate-ping" />
                <Send className="w-12 h-12 relative z-10" />
                <span className="text-sm font-bold relative z-10">Enviar</span>
              </button>
              <p className="text-center text-red-600 font-bold text-2xl font-mono">
                🔴 {String(Math.floor(seconds/60)).padStart(2,'0')}:{String(seconds%60).padStart(2,'0')}
              </p>
              <button
                onClick={cancelRecord}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium flex items-center gap-2"
                data-testid="walkie-cancel-btn"
              >
                <Trash2 className="w-4 h-4" /> Cancelar
              </button>
              <p className="text-xs text-gray-500 text-center">Grabando... Tocá el círculo verde para enviar</p>
            </>
          )}

          {state === 'uploading' && (
            <div className="text-center py-8 flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
              <p className="text-orange-600 font-medium">Enviando...</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PTTWalkieRecorder;
